
import { useEffect, useRef } from 'react';

/**
 * Hook to apply dynamic CSS variables or styles directly to a DOM element via ref,
 * bypassing React's `style` prop to avoid "no inline styles" lint errors for
 * strictly dynamic values (like user-generated colors).
 * 
 * @param styles - Record of CSS properties or variables to apply.
 * @returns RefObject to attach to the target element.
 */
export const useDynamicStyle = <T extends Element & { style: CSSStyleDeclaration }>(styles: Record<string, string | number | undefined | null>) => {
    const ref = useRef<T>(null);

    useEffect(() => {
        const el = ref.current;
        if (el) {
            Object.entries(styles).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    el.style.setProperty(key, String(value));
                } else {
                    el.style.removeProperty(key);
                }
            });
        }
    }, [styles]);

    return ref;
};
